import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { SettingsContext } from '../contexts/index.jsx';
import { fetchUserById, notifyUserUpdated } from "../helpers/userEditTab";
import { HeaderCard, ContactTable, ContactWizard, useUsersTabs, UserDetailDrawer } from "./Users/index.jsx";
import { Icon, PageFilterBar, ScopeSelect, SearchInput, Segmented, Toggle } from "../components/ui";
import { PageHeader, PageLayout } from "../components/layout";
import { useStoreWriteGuard } from "../helpers/storeScope";
import { isMerchantType } from "../helpers/Data";
import { HOME_PATH } from "../core/config/dashboard.js";

// Fontaine M54D — the toolbar is the shared PageFilterBar (`.rc-filterbar`, the row Buy Alerts
// ships): a fixed-length search pill on the left, then status and sort as ScopeSelect dropdowns
// pinned right — one control shape for every filter on the page, as on Manage Listings.
// Still not Segmented (locked by Guillaume): both are dropdowns with their own panel.
//
// The sort keys and the status values below are the API's, unchanged: `created_at` · `turnover` ·
// `last_purchase`, and `show_all` · `claimed` · `unclaimed` · `shopify`. Nothing about the
// request — its body, its 20-row pages, its default `created_at desc`, the 3-character floor or
// the CRM zero-call gate — moved in this milestone. See tests/views/UsersToolbarContract.test.jsx.
const SORT_OPTIONS = [
  { field: "created_at", direction: "desc", label: "Last Created" },
  { field: "turnover", direction: "asc", label: "Turnover: Low To High" },
  { field: "turnover", direction: "desc", label: "Turnover: High To Low" },
  { field: "last_purchase", direction: "desc", label: "Last purchase: New first" },
];

// M54D — status and sort are both "pick one label" controls, so both are the
// shared ScopeSelect now: same ghost trigger as Manage Listings and Buy Alerts,
// instead of two differently-shaped boxed fields on the row. `show_all` is the
// absence of a selection, so it is not an option row.
// The quick filters the page has always had — `applyQuickFilter` drives them — but which were
// only reachable by clicking the verification alert card's "View". Entering "pending" that way
// left no visible way back out, and no sign of which slice you were looking at. They are a
// four-way exclusive choice, so they surface as `Segmented` above the table.
const QUICK_FILTERS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending ID" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const STATUS_SCOPE_OPTIONS = [
  { key: "claimed", label: "Claimed" },
  { key: "unclaimed", label: "Unclaimed" },
  { key: "shopify", label: "Shopify" },
];

// The API's own sort keys, encoded one way — `field|direction` never leaves this
// file, exactly as SortSelect used to guarantee.
const SORT_SCOPE_OPTIONS = SORT_OPTIONS.map((o) => ({
  key: `${o.field}|${o.direction}`,
  label: o.label,
}));

const PAGE_COPY = {
  client: {
    title: "Clients",
    subtitle: "Manage client profiles, verification and activity",
  },
  merchant: {
    title: "Merchants",
    subtitle: "Manage merchant, supplier and personal shopper accounts",
  },
  expert: {
    title: "Experts",
    subtitle: "Manage expert and concierge profiles",
  },
  supplier: {
    title: "Merchants",
    subtitle: "Manage merchant, supplier and personal shopper accounts",
  },
};

const Users = ({ action = "index" }) => {
  const history = useHistory();
  const { type } = useParams();
  const location = useLocation();
  const settings = useContext(SettingsContext);
  const { guardProps: storeGuard } = useStoreWriteGuard();

  const [filters, setFilters] = useState({ type });
  const [searchQuery, setSearchQuery] = useState("");
  // List or cards. Presentation only — it changes no request, no filter and no sort, so it is
  // deliberately not in the URL: sharing a link to a person's directory should not also impose
  // how the recipient likes to look at it.
  const [view, setView] = useState("list");
  // The sort the list is currently asking for. Held as the API's own `{ field, direction }` pair
  // rather than a `"field|direction"` string — SortSelect owns that encoding now, so the page
  // never parses it back apart on the way into the request.
  const [sort, setSort] = useState({ field: "created_at", direction: "desc" });
  const [extraFilter, setExtraFilter] = useState("show_all");
  const [quickFilter, setQuickFilter] = useState("all");
  const [inStock, setInStock] = useState("false");
  const [selectedContact, setSelectedContact] = useState(null);
  const [tableRefresh, setTableRefresh] = useState(0);
  const [, setPendingCount] = useState(0);

  const pageCopy = PAGE_COPY[type] || PAGE_COPY.client;
  const usersTabs = useUsersTabs();

  // Deep link — `/users/:type?userId=123` opens this user's edit form straight
  // away. It is the URL the flows' edit icon opens in a new tab, and it reuses
  // the existing drawer rather than adding a second form.
  const deepLinkUserId = useMemo(
    () => new URLSearchParams(location.search).get("userId"),
    [location.search]
  );

  const onSearchQueryChange = (query) => setSearchQuery(query);

  const onContactCreated = (user) => {
    if (user.type === "expert" || user.type === "concierge") {
      history.push(`/users/expert${settings.iframeQueryParams}`);
    } else if (isMerchantType(user.type)) {
      history.push(`/users/merchant${settings.iframeQueryParams}`);
    } else {
      history.push(`/users/client?${settings.iframeQueryParams}`);
    }
  };

  // The status filter and the pending-KYC quick filter are two views of the same request pair:
  // choosing a status always clears `pending_id_verification`, exactly as before.
  const onStatusFilterChange = (value) => {
    setExtraFilter(value);
    setQuickFilter("all");
    setFilters((prev) => ({
      ...prev,
      extra_filter: value,
      pending_id_verification: false,
    }));
  };

  const applyQuickFilter = (key) => {
    setQuickFilter(key);
    if (key === "all") {
      setExtraFilter("show_all");
      setFilters((prev) => ({
        ...prev,
        extra_filter: "show_all",
        pending_id_verification: false,
      }));
      return;
    }
    if (key === "pending") {
      setExtraFilter("show_all");
      setFilters((prev) => ({
        ...prev,
        extra_filter: "show_all",
        pending_id_verification: true,
      }));
      return;
    }
    if (key === "active") {
      setExtraFilter("claimed");
      setFilters((prev) => ({
        ...prev,
        extra_filter: "claimed",
        pending_id_verification: false,
      }));
      return;
    }
    if (key === "inactive") {
      setExtraFilter("unclaimed");
      setFilters((prev) => ({
        ...prev,
        extra_filter: "unclaimed",
        pending_id_verification: false,
      }));
    }
  };

  const switchType = () => {
    setInStock(!inStock);
    if (filters.type === "supplier") {
      setFilters((prev) => ({ ...prev, type: "merchant" }));
    } else if (filters.type === "merchant") {
      setFilters((prev) => ({ ...prev, type: "supplier" }));
    }
  };

  const listParams = useMemo(
    () => ({ searchQuery, fetchLimit: 20, filters, sort }),
    [searchQuery, filters, sort]
  );

  // Reset only on an actual tab switch. Running this on mount too would clear
  // a `?userId=` selection the moment it was hydrated.
  const lastTypeRef = useRef(type);
  useEffect(() => {
    if (lastTypeRef.current === type) return;
    lastTypeRef.current = type;
    setFilters({ type });
    setSelectedContact(null);
    setQuickFilter("all");
    setExtraFilter("show_all");
    setSearchQuery("");
  }, [type]);

  // The drawer needs the FULL contact (UserDetailPanel reads every field), so a
  // deep link has to re-read it rather than reconstruct it from the list row.
  useEffect(() => {
    if (!deepLinkUserId) return undefined;

    let cancelled = false;
    fetchUserById(deepLinkUserId)
      .then((user) => {
        if (!cancelled && user) setSelectedContact(user);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [deepLinkUserId]);

  const closeDrawer = () => setSelectedContact(null);

  const onContactUpdated = (contact) => {
    setSelectedContact(contact);
    setTableRefresh((n) => n + 1);
    // Announce the save so any flow tab showing this user re-reads it.
    notifyUserUpdated(contact);
  };

  return (
    // PageLayout supplies the same `.rc-page` shell Manage Listings uses, so the
    // header / tab rows span the full content width and sit on the same gutter.
    <PageLayout
      className={`users-dark${action === "create" ? " users-dark--create" : ""}`}
    >
      {action === "index" && (
        /* Shared page chrome, at page level like Listings — page name +
           description, then the section tabs and the Create action. Replaces the
           page-local `users-tab-bar` + `users-page-header` pair. */
        <PageHeader
          title="User CRM"
          description={pageCopy.subtitle}
          backTo={HOME_PATH}
          tabs={usersTabs}
          actions={[
            {
              label: "+ Create user",
              to: "/users/create",
              variant: "primary",
              size: "lg",
              className: "rc-ui-btn--hero",
              "data-btn": "user-create",
              // A user/client record is store-owned; from "Master Store" there
              // is no single store to create it in. See useStoreWriteGuard.
              ...storeGuard,
            },
          ]}
        />
      )}

      {action === "create" && (
        <PageHeader title="Create New User" onBack={() => history.goBack()} />
      )}

      <div className="users-shell">

        {action === "create" && (
          <div className="users-create-wrap">
            <ContactWizard
              className="users-create-form"
              afterSubmit={onContactCreated}
              submitLabel="Create user"
            />
          </div>
        )}

        {action === "index" && (
          <div className={`users-workspace${selectedContact ? " users-workspace--drawer-open" : ""}`}>
            <div className="users-workspace__list">
              <HeaderCard
                setFilters={setFilters}
                onPendingCount={setPendingCount}
                onActivatePending={() => applyQuickFilter("pending")}
                isPendingActive={quickFilter === "pending"}
              />

              <PageFilterBar
                className="users-toolbar"
                label={`Filter ${pageCopy.title.toLowerCase()}`}
                search={(
                  <SearchInput
                    size="lg"
                    // The placeholder is a hint, not a name: it disappears the moment there is
                    // text, and a screen reader announcing "Search by name, email or ID" says
                    // nothing about WHAT is being searched. The searchbox gets its own name.
                    aria-label="Search users"
                    placeholder="Search by name, email or ID"
                    onChange={(e) => onSearchQueryChange(e.target.value)}
                    onClear={() => onSearchQueryChange("")}
                    value={searchQuery}
                  />
                )}
              >
                {/* Above the table with the rest of the filters, and compact so it sits on the
                    row rather than dominating it — the same treatment the Orders period control
                    uses. */}
                <Segmented
                  label="Show"
                  size="sm"
                  className="users-quickfilter"
                  value={quickFilter}
                  onChange={applyQuickFilter}
                  options={QUICK_FILTERS}
                />

                <ScopeSelect
                  label="Status"
                  placeholder="Show All"
                  multiple={false}
                  options={STATUS_SCOPE_OPTIONS}
                  value={extraFilter === "show_all" ? null : extraFilter}
                  onChange={(next) => onStatusFilterChange(next ?? "show_all")}
                />

                <ScopeSelect
                  label="Sort"
                  placeholder="Sort"
                  multiple={false}
                  options={SORT_SCOPE_OPTIONS}
                  value={`${sort.field}|${sort.direction}`}
                  onChange={(next) => {
                    // Re-picking the current sort clears nothing — the list is
                    // always sorted by something, so it falls back to the default.
                    const [field, direction] = (next ?? "created_at|desc").split("|");
                    setSort({ field, direction });
                  }}
                />

                {type === "merchant" ? (
                  <Toggle
                    label="Suppliers"
                    labelPosition="start"
                    checked={!inStock}
                    onChange={() => switchType()}
                  />
                ) : null}

                {/* Grid / list. `Segmented` is the app's exclusive-choice control and already
                    names this pair in its own contract, so it carries the roving focus, the
                    arrow keys and the radiogroup semantics — nothing bespoke here. Icon-only:
                    the two shapes read faster than the words at this size, and the accessible
                    name still comes from each option's `label`. */}
                <Segmented
                  label="View"
                  size="md"
                  iconOnly
                  value={view}
                  onChange={setView}
                  className="users-viewswitch"
                  options={[
                    { value: "grid", label: "Grid", icon: <Icon name="LayoutGrid" size="sm" /> },
                    { value: "list", label: "List", icon: <Icon name="List" size="sm" /> },
                  ]}
                />
              </PageFilterBar>

              {/* M54E — the directory is a stacked list now, so the banded
                  table container is gone with the table. */}
              <div className="users-list-container">
                <ContactTable
                  key={tableRefresh}
                  {...listParams}
                  minSearchLength={3}
                  onOpenContact={setSelectedContact}
                  selectedContactId={selectedContact?.id}
                  view={view}
                />
              </div>
            </div>

            <UserDetailDrawer
              open={!!selectedContact}
              contact={selectedContact}
              onClose={closeDrawer}
              onContactUpdated={onContactUpdated}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default Users;
